import { fmt } from "./numberInput";
import type { CalcStep } from "@/lib/calc/steps";

/** `steps` is the ordered working behind `value` (2 to 3 lines); it is built from the same numbers, never recalculated. */
export type Calc = { value: number; text: string; working: string; steps?: CalcStep[] } | { error: string };

/** Percent, per mille (parts per thousand) and basis points (parts per ten thousand). */
export type PctUnit = "pct" | "permille" | "bps";
export const UNIT_PER: Record<PctUnit, number> = { pct: 100, permille: 1000, bps: 10000 };
export const UNIT_SYMBOL: Record<PctUnit, string> = { pct: "%", permille: "‰", bps: " bps" };
export const UNIT_NAME: Record<PctUnit, string> = { pct: "percent", permille: "per mille", bps: "basis points" };
const sym = (u: PctUnit) => UNIT_SYMBOL[u];

/** p units of `base`: p / per x base. `unit` defaults to percent. */
export const pctOf = (p: number, base: number, unit: PctUnit = "pct"): Calc => {
  const per = UNIT_PER[unit], dec = p / per, v = dec * base;
  return {
    value: v, text: fmt(v), working: `${fmt(p)} ÷ ${per} × ${fmt(base)}`,
    steps: [
      { label: `Turn ${UNIT_NAME[unit]} into a decimal`, formula: `${UNIT_NAME[unit]} / ${per}`, inputs: { [UNIT_NAME[unit]]: fmt(p) }, result: fmt(dec) },
      { label: "Multiply by the number", formula: "decimal x number", inputs: { decimal: fmt(dec), number: fmt(base) }, result: fmt(v) },
    ],
  };
};

/** What percent (or per mille / basis points) is `a` of `b`. */
export function isWhatPct(a: number, b: number, unit: PctUnit = "pct"): Calc {
  if (b === 0) return { error: "Can't work out a percentage of 0 (division by zero)." };
  const per = UNIT_PER[unit], ratio = a / b, v = ratio * per;
  return {
    value: v, text: `${fmt(v, 2)}${sym(unit)}`, working: `${fmt(a)} ÷ ${fmt(b)} × ${per}`,
    steps: [
      { label: "Divide the part by the whole", formula: "part / whole", inputs: { part: fmt(a), whole: fmt(b) }, result: fmt(ratio) },
      { label: `Multiply by ${per}`, formula: `ratio x ${per}`, inputs: { ratio: fmt(ratio) }, result: `${fmt(v, 2)}${sym(unit)}` },
    ],
  };
}

/** Solve for the number when the percentage and the result are known: number = result / (p / per). */
export function solveBase(p: number, result: number, unit: PctUnit = "pct"): Calc {
  if (p === 0) return { error: "A percentage of 0 gives 0 for any number, so the number can't be worked out." };
  const per = UNIT_PER[unit], dec = p / per, v = result / dec;
  return {
    value: v, text: fmt(v), working: `${fmt(result)} ÷ (${fmt(p)} ÷ ${per})`,
    steps: [
      { label: `Turn ${UNIT_NAME[unit]} into a decimal`, formula: `${UNIT_NAME[unit]} / ${per}`, inputs: { [UNIT_NAME[unit]]: fmt(p) }, result: fmt(dec) },
      { label: "Divide the result by the decimal", formula: "result / decimal", inputs: { result: fmt(result), decimal: fmt(dec) }, result: fmt(v) },
    ],
  };
}

/** Which of the three boxes of "p of number = result" was solved. `overdetermined` means all three were filled in. */
export type Solved = { field: "pct" | "base" | "result"; calc: Calc; overdetermined: boolean };
/**
 * Solve any two of three: percentage, number, result. Leave one empty (null) and it is solved from the other two.
 * With all three filled the result is recomputed from the other two and `overdetermined` is true; with fewer than two, null.
 */
export function solvePercentOf(p: number | null, base: number | null, result: number | null, unit: PctUnit = "pct"): Solved | null {
  const have = [p, base, result].filter((x) => x !== null).length;
  if (have < 2) return null;
  if (p === null) return { field: "pct", calc: isWhatPct(result!, base!, unit), overdetermined: false };
  if (base === null) return { field: "base", calc: solveBase(p, result!, unit), overdetermined: false };
  return { field: "result", calc: pctOf(p, base, unit), overdetermined: have === 3 && result !== null };
}

/** Percent change when the start and the end are known, the start and the change are known, or the end and the change are known. */
export function solveChange(from: number | null, to: number | null, pct: number | null): { field: "from" | "to" | "pct"; calc: Calc; overdetermined: boolean } | null {
  const have = [from, to, pct].filter((x) => x !== null).length;
  if (have < 2) return null;
  if (pct === null) return { field: "pct", calc: pctChange(from!, to!), overdetermined: false };
  if (to === null) {
    const v = from! * (1 + pct / 100);
    return { field: "to", overdetermined: false, calc: { value: v, text: fmt(v, 4), working: `${fmt(from!)} × (1 + ${fmt(pct)} ÷ 100)`, steps: [
      { label: "Turn the change into a multiplier", formula: "1 + change / 100", inputs: { change: `${fmt(pct)}%` }, result: fmt(1 + pct / 100) },
      { label: "Multiply the starting value", formula: "start x multiplier", inputs: { start: fmt(from!), multiplier: fmt(1 + pct / 100) }, result: fmt(v, 4) },
    ] } };
  }
  if (from === null) {
    const k = 1 + pct / 100;
    if (k === 0) return { field: "from", overdetermined: false, calc: { error: "A change of -100% always ends at 0, so the starting value can't be worked out." } };
    const v = to / k;
    return { field: "from", overdetermined: false, calc: { value: v, text: fmt(v, 4), working: `${fmt(to)} ÷ (1 + ${fmt(pct)} ÷ 100)`, steps: [
      { label: "Turn the change into a multiplier", formula: "1 + change / 100", inputs: { change: `${fmt(pct)}%` }, result: fmt(k) },
      { label: "Divide the new value by the multiplier", formula: "new value / multiplier", inputs: { "new value": fmt(to), multiplier: fmt(k) }, result: fmt(v, 4) },
    ] } };
  }
  return { field: "pct", calc: pctChange(from, to), overdetermined: true };
}

/** For values that are themselves percentages: the change in percentage points and the relative change in percent. 10% to 12% is +2 pp and +20%. */
export function pctPointChange(from: number, to: number): Calc {
  const pp = to - from;
  const sign = (n: number) => (n >= 0 ? "+" : "-");
  const ppText = `${sign(pp)}${fmt(Math.abs(pp), 4)} pp`;
  if (from === 0) return { value: pp, text: ppText, working: `${fmt(to)} − ${fmt(from)}`, steps: [
    { label: "Subtract: change in percentage points", formula: "new % - old %", inputs: { new: `${fmt(to)}%`, old: `${fmt(from)}%` }, result: ppText },
    { label: "Relative change", formula: "change / old value x 100", inputs: { old: "0%" }, result: "Not defined: the old value is 0" },
  ] };
  const rel = (pp / Math.abs(from)) * 100;
  const relText = `${sign(rel)}${fmt(Math.abs(rel), 2)}%`;
  return { value: pp, text: `${ppText}, ${relText}`, working: `${fmt(to)} − ${fmt(from)}; ${fmt(pp)} ÷ ${fmt(Math.abs(from))} × 100`, steps: [
    { label: "Subtract: change in percentage points", formula: "new % - old %", inputs: { new: `${fmt(to)}%`, old: `${fmt(from)}%` }, result: ppText },
    { label: "Relative change in percent", formula: "points change / old value x 100", inputs: { "points change": fmt(pp, 4), old: `${fmt(from)}%` }, result: relText },
  ] };
}

/** Change relative to the size of the starting value, so a rise from a negative number is still a rise. */
export function pctChange(from: number, to: number): Calc {
  if (from === 0) return { error: to === 0 ? "No change (both values are 0)." : "Undefined: the starting value is 0, so the change can't be given as a percentage." };
  const v = ((to - from) / Math.abs(from)) * 100;
  return {
    value: v, text: `${v >= 0 ? "+" : ""}${fmt(v, 2)}%`, working: `(${fmt(to)} − ${fmt(from)}) ÷ ${fmt(Math.abs(from))} × 100`,
    steps: [
      { label: "Find the change", formula: "new value - starting value", inputs: { "new value": fmt(to), "starting value": fmt(from) }, result: fmt(to - from) },
      { label: "Divide by the starting value and multiply by 100", formula: "change / |starting value| x 100", inputs: { change: fmt(to - from), "starting value": fmt(Math.abs(from)) }, result: `${v >= 0 ? "+" : ""}${fmt(v, 2)}%` },
    ],
  };
}

export const changeBy = (n: number, p: number, up: boolean): Calc => {
  const v = n * (1 + (up ? p : -p) / 100);
  return {
    value: v, text: fmt(v, 4), working: `${fmt(n)} × (1 ${up ? "+" : "−"} ${fmt(p)} ÷ 100)`,
    steps: [
      { label: "Turn the percentage into a multiplier", formula: `1 ${up ? "+" : "-"} percentage / 100`, inputs: { percentage: `${fmt(p)}%` }, result: fmt(1 + (up ? p : -p) / 100) },
      { label: "Multiply the number", formula: "number x multiplier", inputs: { number: fmt(n), multiplier: fmt(1 + (up ? p : -p) / 100) }, result: fmt(v, 4) },
    ],
  };
};

/** "After a 20% increase it's 120: what was the original?" */
export function reversePct(final: number, p: number, up: boolean): Calc {
  if (!up && p > 100) return { error: "A decrease can't be more than 100%. Try a value from 0 to 100." };
  const f = 1 + (up ? p : -p) / 100;
  if (f === 0) return { error: "A 100% decrease leaves 0, so the original can't be recovered." };
  const v = final / f;
  return {
    value: v, text: fmt(v, 4), working: `${fmt(final)} ÷ (1 ${up ? "+" : "−"} ${fmt(p)} ÷ 100)`,
    steps: [
      { label: "Turn the percentage into a multiplier", formula: `1 ${up ? "+" : "-"} percentage / 100`, inputs: { percentage: `${fmt(p)}%` }, result: fmt(f) },
      { label: "Divide the final value by the multiplier", formula: "final value / multiplier", inputs: { "final value": fmt(final), multiplier: fmt(f) }, result: fmt(v, 4) },
    ],
  };
}

/** Symmetric difference between two values (relative to their average). */
export function pctDifference(a: number, b: number): Calc {
  if (a + b === 0) return { error: "Can't compare: the average of the two values is 0." };
  const v = (Math.abs(a - b) / ((Math.abs(a) + Math.abs(b)) / 2)) * 100;
  return {
    value: v, text: `${fmt(v, 2)}%`, working: `|${fmt(a)} − ${fmt(b)}| ÷ ((${fmt(Math.abs(a))} + ${fmt(Math.abs(b))}) ÷ 2) × 100`,
    steps: [
      { label: "Find the gap and the average", formula: "|a - b| and (|a| + |b|) / 2", inputs: { a: fmt(a), b: fmt(b) }, result: `gap ${fmt(Math.abs(a - b))}, average ${fmt((Math.abs(a) + Math.abs(b)) / 2)}` },
      { label: "Divide the gap by the average and multiply by 100", formula: "gap / average x 100", inputs: { gap: fmt(Math.abs(a - b)), average: fmt((Math.abs(a) + Math.abs(b)) / 2) }, result: `${fmt(v, 2)}%` },
    ],
  };
}
